import React, { useState, useEffect } from 'react';
import { 
  Search, Plus, Edit, Eye, EyeOff, Trash2, Building2, Mail, Phone,
  Globe, User, Calendar, Clock, Filter, X
} from 'lucide-react';
import { useAuth } from '../../../../context/AuthContext';

const BusinessUnits = () => {
  const API_URL = process.env.REACT_APP_API_URL || 'https://ksp3plhq.com/api';
  const { currentUser } = useAuth();

  // State
  const [businessUnits, setBusinessUnits] = useState([]);
  const [filteredBusinessUnits, setFilteredBusinessUnits] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showBusinessUnitForm, setShowBusinessUnitForm] = useState(false);
  const [currentBusinessUnit, setCurrentBusinessUnit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch business units
  useEffect(() => {
    fetchBusinessUnits();
  }, []);

  const fetchBusinessUnits = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/business-units`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch business units: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Transform data to match component structure
      const transformedData = data.map(unit => ({
        ...unit,
        phoneNumber: unit.phone_number || '',
        emails: [
          unit.email1,
          unit.email2,
          unit.email3
        ].filter(Boolean) // Remove null/empty emails
      }));
      
      setBusinessUnits(transformedData);
      setFilteredBusinessUnits(transformedData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load business units. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter business units when search term or status filter changes
  useEffect(() => {
    let results = [...businessUnits];
    
    if (searchTerm) {
      const searchTermLower = searchTerm.toLowerCase();
      results = results.filter(unit => 
        unit.name?.toLowerCase().includes(searchTermLower) ||
        unit.identifier?.toLowerCase().includes(searchTermLower) ||
        unit.description?.toLowerCase().includes(searchTermLower) ||
        unit.manager?.toLowerCase().includes(searchTermLower)
      );
    }
    
    if (statusFilter !== 'all') {
      const isActive = statusFilter === 'active';
      results = results.filter(unit => unit.is_active === (isActive ? 1 : 0));
    }
    
    setFilteredBusinessUnits(results);
  }, [businessUnits, searchTerm, statusFilter]);
  
  // Business Unit CRUD operations
const addBusinessUnit = async (businessUnit) => {
  try {
    const token = localStorage.getItem('token');
    
    // Transform data for API
    const apiData = {
      name: businessUnit.name,
      identifier: businessUnit.identifier,
      description: businessUnit.description || '',
      address: businessUnit.address || '',
      email1: businessUnit.emails[0] || '',
      ...(businessUnit.emails[1] ? { email2: businessUnit.emails[1] } : {}),
      ...(businessUnit.emails[2] ? { email3: businessUnit.emails[2] } : {}),
      phone_number: businessUnit.phoneNumber || '',
      notes: businessUnit.notes || '',
      manager: businessUnit.manager || '',
      website: businessUnit.website || '',
      is_active: businessUnit.is_active
    };
    
    console.log('Sending data to API:', apiData);
    
    const response = await fetch(`${API_URL}/business-units`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(apiData)
    });
    
    const responseData = await response.json();
    console.log('API response:', responseData);
    
    if (!response.ok) {
      throw new Error(responseData.message || responseData.errors?.[0]?.msg || 'Failed to create business unit');
    }
    
    // Transform the response to match frontend structure
    const newUnit = {
      ...responseData.businessUnit,
      phoneNumber: responseData.businessUnit.phone_number,
      emails: [
        responseData.businessUnit.email1,
        responseData.businessUnit.email2,
        responseData.businessUnit.email3
      ].filter(Boolean)
    };
    
    setBusinessUnits([...businessUnits, newUnit]);
    setShowBusinessUnitForm(false);
    setCurrentBusinessUnit(null);
  } catch (error) {
    console.error('Error creating business unit:', error);
    alert(`Error: ${error.message}`);
  }
};

  const updateBusinessUnit = async (updatedBusinessUnit) => {
    try {
      const token = localStorage.getItem('token');
      
          // Transform data for API
        const apiData = {
          name: updatedBusinessUnit.name,              
          identifier: updatedBusinessUnit.identifier,   
          description: updatedBusinessUnit.description || '',
          address: updatedBusinessUnit.address || '',
          email1: updatedBusinessUnit.emails[0] || '',  
          ...(updatedBusinessUnit.emails[1] ? { email2: updatedBusinessUnit.emails[1] } : {}),
          ...(updatedBusinessUnit.emails[2] ? { email3: updatedBusinessUnit.emails[2] } : {}),
          phone_number: updatedBusinessUnit.phoneNumber || '',
          notes: updatedBusinessUnit.notes || '',
          manager: updatedBusinessUnit.manager || '',
          website: updatedBusinessUnit.website || '',
          is_active: updatedBusinessUnit.is_active
        };
      
      const response = await fetch(`${API_URL}/business-units/${updatedBusinessUnit.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(apiData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update business unit');
      }
      
      const data = await response.json();
      
      // Transform the response to match frontend structure
      const updatedUnit = {
        ...data.businessUnit,
        phoneNumber: data.businessUnit.phone_number,
        emails: [
          data.businessUnit.email1,
          data.businessUnit.email2,
          data.businessUnit.email3
        ].filter(Boolean)
      };
      
      setBusinessUnits(businessUnits.map(unit => 
        unit.id === updatedBusinessUnit.id ? updatedUnit : unit
      ));
      setShowBusinessUnitForm(false);
      setCurrentBusinessUnit(null);
    } catch (error) {
      console.error('Error updating business unit:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const toggleBusinessUnitStatus = async (id) => {
    try {
      const unit = businessUnits.find(u => u.id === id);
      if (!unit) return;
      
      const token = localStorage.getItem('token');
      const newStatus = unit.is_active === 1 ? 0 : 1;
      
      const response = await fetch(`${API_URL}/business-units/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: newStatus })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update business unit status');
      }
      
      setBusinessUnits(businessUnits.map(unit => 
        unit.id === id ? { ...unit, is_active: newStatus } : unit
      ));
    } catch (error) {
      console.error('Error toggling business unit status:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const deleteBusinessUnit = async (id) => {
    if (window.confirm('Are you sure you want to delete this business unit? This action cannot be undone.')) {
      try {
        const token = localStorage.getItem('token');
        
        const response = await fetch(`${API_URL}/business-units/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to delete business unit');
        }
        
        setBusinessUnits(businessUnits.filter(unit => unit.id !== id));
      } catch (error) {
        console.error('Error deleting business unit:', error);
        alert(`Error: ${error.message}`);
      }
    }
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };
  
  // Get username by ID
  const getUsernameById = (id) => {
    return `User #${id}`;
  };
  
  // Form handlers
  const handleNewBusinessUnit = () => {
    setCurrentBusinessUnit({
      name: '',
      identifier: '',
      description: '',
      address: '',
      emails: [''],
      phoneNumber: '',
      notes: '',
      manager: '',
      website: '',
      is_active: 1
    });
    setShowBusinessUnitForm(true);
  };
  
  const handleEditBusinessUnit = (unit) => {
    setCurrentBusinessUnit({ ...unit });
    setShowBusinessUnitForm(true);
  };
  
  const resetForm = () => {
    setShowBusinessUnitForm(false);
    setCurrentBusinessUnit(null);
  };
  
  // Email field handlers
  const addEmailField = () => {
    if (currentBusinessUnit.emails.length >= 3) {
      alert("Maximum of 3 email addresses allowed");
      return;
    }
    
    setCurrentBusinessUnit({
      ...currentBusinessUnit,
      emails: [...currentBusinessUnit.emails, '']
    });
  };
  
  const removeEmailField = (index) => {
    setCurrentBusinessUnit({
      ...currentBusinessUnit,
      emails: currentBusinessUnit.emails.filter((_, i) => i !== index)
    });
  };
  
  const updateEmailField = (index, value) => {
    const updatedEmails = [...currentBusinessUnit.emails];
    updatedEmails[index] = value;
    setCurrentBusinessUnit({
      ...currentBusinessUnit,
      emails: updatedEmails
    });
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-slate-200">Business Units Management</h2>
      
      {/* Header with search and filters */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div className="relative flex-grow max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search business units..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-md 
                       text-slate-200 text-sm placeholder-slate-400
                       focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter className="text-slate-400 w-4 h-4" />
            <span className="text-sm text-slate-400">Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-slate-700 border border-slate-600 rounded-md text-slate-300 
                       text-sm py-1 px-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          
          <button 
            onClick={handleNewBusinessUnit}
            className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 
                    text-white rounded-md text-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Business Unit
          </button>
        </div>
        
        {/* Error Message */}
        {error && (
          <div className="bg-red-900/30 border border-red-800 text-red-300 px-4 py-3 rounded-md mb-6">
            <p>{error}</p>
            <button 
              onClick={fetchBusinessUnits} 
              className="mt-2 text-sm text-blue-400 hover:text-blue-300"
            >
              Try again
            </button>
          </div>
        )}
        
        {/* Business Units List */}
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredBusinessUnits.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            No business units found. Create a new business unit to get started.
          </div>
        ) : (
          <div className="space-y-4">
            {filteredBusinessUnits.map(unit => (
              <div 
                key={unit.id} 
                className="bg-slate-700/40 border border-slate-600 rounded-md p-4"
              >
                <div className="flex justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-medium text-slate-200">
                        {unit.name}
                      </h3>
                      <span className="text-sm font-mono bg-slate-600/50 px-2 py-0.5 rounded text-slate-300">
                        {unit.identifier}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                        ${unit.is_active === 1 ? 
                          'bg-green-900/30 text-green-300 border border-green-800/50' : 
                          'bg-slate-900/50 text-slate-400 border border-slate-700'}`}
                      >
                        {unit.is_active === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    <p className="text-sm text-slate-400 mb-4">{unit.description}</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-300">
                      <div>
                        <div className="flex items-start mb-2">
                          <User className="w-4 h-4 mt-0.5 mr-2 text-slate-400" />
                          <span><span className="text-slate-400">Manager:</span> {unit.manager}</span>
                        </div>
                        <div className="flex items-start mb-2">
                          <Building2 className="w-4 h-4 mt-0.5 mr-2 text-slate-400" />
                          <span>{unit.address}</span>
                        </div>
                        <div className="flex items-start mb-2">
                          <Phone className="w-4 h-4 mt-0.5 mr-2 text-slate-400" />
                          <span>{unit.phoneNumber}</span>
                        </div>
                        {unit.website && (
                          <div className="flex items-start mb-2">
                            <Globe className="w-4 h-4 mt-0.5 mr-2 text-slate-400" />
                            <a href={unit.website} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">{unit.website}</a>
                          </div>
                        )}
                      </div>
                      
                      <div>
                        <div className="flex items-start mb-2">
                          <Mail className="w-4 h-4 mt-0.5 mr-2 text-slate-400" />
                          <div className="flex flex-col">
                            {unit.emails.map((email, index) => (
                              <span key={index} className="block">{email}</span>
                            ))}
                          </div>
                        </div>
                        
                        <div className="text-xs text-slate-400 mt-4">
                          <div className="flex items-center mb-1">
                            <Calendar className="w-3 h-3 mr-1" />
                            <span>Created: {formatDate(unit.created_at)} by {getUsernameById(unit.created_by)}</span>
                          </div>
                          <div className="flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            <span>Updated: {formatDate(unit.updated_at)} by {getUsernameById(unit.updated_by)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {unit.notes && (
                      <div className="mt-4 pt-4 border-t border-slate-600/50">
                        <h4 className="text-sm font-medium text-slate-300 mb-1">Notes</h4>
                        <p className="text-sm text-slate-400">{unit.notes}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col space-y-1 ml-4">
                    <button
                      onClick={() => handleEditBusinessUnit(unit)}
                      className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-600/30 rounded"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => toggleBusinessUnitStatus(unit.id)}
                      className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-600/30 rounded"
                      title={unit.is_active === 1 ? "Deactivate" : "Activate"}
                    >
                      {unit.is_active === 1 ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => deleteBusinessUnit(unit.id)}
                      className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-600/30 rounded"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Business Unit Form Modal */}
      {showBusinessUnitForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-700 flex justify-between items-center sticky top-0 bg-slate-800 z-10">
              <h3 className="text-lg font-medium text-slate-200">
                {currentBusinessUnit.id ? 'Edit Business Unit' : 'New Business Unit'}
              </h3>
              <button 
                onClick={resetForm}
                className="text-slate-400 hover:text-slate-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              currentBusinessUnit.id ? updateBusinessUnit(currentBusinessUnit) : addBusinessUnit(currentBusinessUnit);
            }}>
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-1">
                      Business Name*
                    </label>
                    <input
                      id="name"
                      type="text"
                      value={currentBusinessUnit.name}
                      onChange={(e) => setCurrentBusinessUnit({...currentBusinessUnit, name: e.target.value})}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md 
                              text-slate-200 placeholder-slate-400
                              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="identifier" className="block text-sm font-medium text-slate-300 mb-1">
                      Identifier (Acronym)*
                    </label>
                    <input
                      id="identifier"
                      type="text"
                      value={currentBusinessUnit.identifier}
                      onChange={(e) => setCurrentBusinessUnit({...currentBusinessUnit, identifier: e.target.value})}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md 
                              text-slate-200 placeholder-slate-400
                              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-slate-300 mb-1">
                    Description
                  </label>
                  <textarea
                    id="description"
                    value={currentBusinessUnit.description}
                    onChange={(e) => setCurrentBusinessUnit({...currentBusinessUnit, description: e.target.value})}
                    rows="2"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md 
                            text-slate-200 placeholder-slate-400
                            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  ></textarea>
                </div>
                
                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-slate-300 mb-1">
                    Address
                  </label>
                  <textarea
                    id="address"
                    value={currentBusinessUnit.address}
                    onChange={(e) => setCurrentBusinessUnit({...currentBusinessUnit, address: e.target.value})}
                    rows="2"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md 
                            text-slate-200 placeholder-slate-400
                            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  ></textarea>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="manager" className="block text-sm font-medium text-slate-300 mb-1">
                      Manager/Primary Contact
                    </label>
                    <input
                      id="manager"
                      type="text"
                      value={currentBusinessUnit.manager}
                      onChange={(e) => setCurrentBusinessUnit({...currentBusinessUnit, manager: e.target.value})}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md 
                              text-slate-200 placeholder-slate-400
                              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="phoneNumber" className="block text-sm font-medium text-slate-300 mb-1">
                      Phone Number
                    </label>
                    <input
                      id="phoneNumber"
                      type="text"
                      value={currentBusinessUnit.phoneNumber}
                      onChange={(e) => setCurrentBusinessUnit({...currentBusinessUnit, phoneNumber: e.target.value})}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md 
                              text-slate-200 placeholder-slate-400
                              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="website" className="block text-sm font-medium text-slate-300 mb-1">
                    Website URL
                  </label>
                  <input
                    id="website"
                    type="url"
                    value={currentBusinessUnit.website}
                    onChange={(e) => setCurrentBusinessUnit({...currentBusinessUnit, website: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md 
                            text-slate-200 placeholder-slate-400
                            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://example.com"
                  />
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="block text-sm font-medium text-slate-300">
                      Email Addresses
                    </label>
                    <button
                      type="button"
                      onClick={addEmailField}
                      className="text-sm text-blue-400 hover:text-blue-300"
                      disabled={currentBusinessUnit.emails.length >= 3}
                    >
                      + Add Email
                    </button>
                  </div>
                  
                  {currentBusinessUnit.emails.map((email, index) => (
                    <div key={index} className="flex mb-2">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => updateEmailField(index, e.target.value)}
                        className="flex-grow px-3 py-2 bg-slate-700 border border-slate-600 rounded-l-md 
                                text-slate-200 placeholder-slate-400
                                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="email@example.com"
                        required={false}
                      />
                      {currentBusinessUnit.emails.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeEmailField(index)}
                          className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-r-md"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  {currentBusinessUnit.emails.length >= 3 && (
                    <p className="text-xs text-slate-400">Maximum of 3 email addresses allowed</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-slate-300 mb-1">
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    value={currentBusinessUnit.notes}
                    onChange={(e) => setCurrentBusinessUnit({...currentBusinessUnit, notes: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md 
                            text-slate-200 placeholder-slate-400
                            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  ></textarea>
                </div>
                
                <div className="flex items-center">
                  <input
                    id="isActive"
                    type="checkbox"
                    checked={currentBusinessUnit.is_active === 1}
                    onChange={(e) => setCurrentBusinessUnit({
                      ...currentBusinessUnit, 
                      is_active: e.target.checked ? 1 : 0
                    })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 
                            border-slate-500 rounded bg-slate-700"
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-slate-300">
                    Active
                  </label>
                </div>
              </div>
              
              <div className="p-4 border-t border-slate-700 flex justify-end space-x-3 sticky bottom-0 bg-slate-800 z-10">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 
                          rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white 
                          rounded-md transition-colors"
                >
                  {currentBusinessUnit.id ? 'Update Business Unit' : 'Create Business Unit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessUnits;
